import { createContext, useEffect, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

import type { BreakpointSize } from '../../styles';
import { Breakpoint } from '../../styles';

export type Viewport = Record<BreakpointSize, boolean>;

const BREAKPOINT_QUERIES: Record<BreakpointSize, string> = Object.entries(
  Breakpoint
)
  .filter(([key]) => Number.isNaN(Number(key)))
  .reduce(
    (acc, [size, breakpoint]) => {
      const key = size.toLowerCase() as BreakpointSize;
      const value = `(min-width: ${breakpoint}px)`;
      return { ...acc, [key]: value };
    },
    {} as Record<BreakpointSize, string>
  );

const EMPTY_VIEWPORT: Viewport = {
  xs: false,
  sm: false,
  md: false,
  lg: false,
  xl: false,
};

export const ViewportContext = createContext<Viewport | undefined>(undefined);

type BreakpointEntry = {
  size: BreakpointSize;
  mediaQueryList: MediaQueryList;
};

/**
 * Creates an array of BreakpointEntry objects, each containing a breakpoint
 * size and its corresponding MediaQueryList. This function is used to set up
 * the media query listeners for the different breakpoints defined in
 * BREAKPOINT_QUERIES.
 */
function createBreakpointEntries(): BreakpointEntry[] {
  const entries = Object.entries(BREAKPOINT_QUERIES) as Array<
    [BreakpointSize, string]
  >;
  return entries.map(([size, query]) => {
    const mediaQueryList =
      typeof window === 'undefined'
        ? ({ matches: false, media: query } as MediaQueryList)
        : window.matchMedia(query);
    return { size, mediaQueryList };
  });
}

/**
 * Reads the current viewport state by checking the matches property of each
 * MediaQueryList in the provided entries. It returns a Viewport object that
 * indicates which breakpoints are currently active based on the media queries.
 */
function readViewport(entries: BreakpointEntry[]): Viewport {
  return entries.reduce((acc, { size, mediaQueryList }) => {
    return {
      ...acc,
      [size]: mediaQueryList.matches,
    };
  }, EMPTY_VIEWPORT);
}

/**
 * Custom hook that uses the `matchMedia` API to determine the current viewport
 * based on the defined breakpoints. It sets up event listeners to update the
 * viewport state whenever the media queries change, allowing components to
 * react to changes in the viewport size.
 */
export function useMatchMediaViewport(enabled: boolean): Viewport {
  const entriesRef = useRef<BreakpointEntry[] | null>(null);
  if (enabled && entriesRef.current === null) {
    entriesRef.current = createBreakpointEntries();
  }

  const [viewport, setViewport] = useState<Viewport>(() =>
    readViewport(entriesRef.current ?? [])
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handler = () => {
      setViewport(readViewport(entriesRef.current ?? []));
    };

    const entries = entriesRef.current ?? [];

    entries.forEach(({ mediaQueryList }) => {
      if (mediaQueryList.addEventListener) {
        mediaQueryList.addEventListener('change', handler);
      } else {
        // Keep compatibility with Safari < v14, which doesn't inherit from `EventTarget`.
        // See https://developer.mozilla.org/en-US/docs/Web/API/MediaQueryList#browser_compatibility
        mediaQueryList.addListener(handler);
      }
    });

    return () => {
      entries.forEach(({ mediaQueryList }) => {
        if (mediaQueryList.removeEventListener) {
          mediaQueryList.removeEventListener('change', handler);
        } else {
          // Keep compatibility with Safari < v14, which doesn't inherit from `EventTarget`.
          // See https://developer.mozilla.org/en-US/docs/Web/API/MediaQueryList#browser_compatibility
          mediaQueryList.removeListener(handler);
        }
      });
    };
  }, [enabled]);

  return viewport;
}

export type ViewportProviderProps = {
  children: ReactNode;
  viewport?: Viewport;
};

export function ViewportProvider({
  children,
  viewport,
}: ViewportProviderProps): ReactElement {
  const matchMediaViewport = useMatchMediaViewport(!viewport);
  const value = viewport ?? matchMediaViewport;

  return (
    <ViewportContext.Provider value={value}>
      {children}
    </ViewportContext.Provider>
  );
}
